import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { _organizationDetails, _vendorLogin } from "Services/api";
import InputField from "components/fields/InputField";
import {
  LoginFormFieldsName,
  loginFormDefaultValues,
  loginFormSchema,
} from "constants/react-hook-form";
import { isPendingVendorSession, storeAdminSession } from "utils/session";
import { Link } from "react-router-dom";

export default function Login() {
  const [loading, setLoading] = useState(false); // State for loading
  const [apiResError, setApiResError] = useState();

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(loginFormSchema),
    defaultValues: loginFormDefaultValues,
    mode: "onSubmit",
  });

  const handleVendorLogin = async (data) => {
    setLoading(true); // Set loading to true before API request
    setApiResError("");
    try {
      const body = {
        email: data[LoginFormFieldsName.EMAIL],
        password: data[LoginFormFieldsName.PASSWORD],
      };
      const response = await _vendorLogin({ body });
      if (response.status) {
        const responseData = response?.data?.data || response?.data || {};
        const userId = responseData?.user_id || responseData?.id;
        const isAdmin = responseData?.type?.includes("ADMIN") ? true : false;
        const storedUser = storeAdminSession(responseData);
        const accessToken = responseData?.access_token || responseData?.token;

        if (isAdmin) {
          window.location.href = `/admin/vendor`;
        } else if (isPendingVendorSession(storedUser)) {
          try {
            await _organizationDetails(accessToken);
            window.location.href = "/admin/pending-approval";
          } catch (organizationError) {
            if (organizationError?.response?.status === 404) {
              window.location.href = "/admin/auth/organization";
              return;
            }
            throw organizationError;
          }
        } else {
          try {
            await _organizationDetails(accessToken);
          } catch (organizationError) {
            const status = organizationError?.response?.status;

            if (status === 404) {
              window.location.href = "/admin/auth/organization";
              return;
            }

            throw organizationError;
          }

          window.location.href = `/admin/${userId}`;
        }
      }
    } catch (error) {
      console.log(error);
      setApiResError(error?.response?.data?.message);
    } finally {
      setLoading(false); // Set loading to false after API request
    }
  };

  return (
    <div className="relative mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      {/* Loader Overlay */}
      {loading && (
        <div className="loader-overlay">
          <span className="loader"></span>
        </div>
      )}

      {/* Sign in section */}
      <form
        className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]"
        onSubmit={handleSubmit(handleVendorLogin)}
        noValidate
      >
        <h4 className="mb-2.5 mt-5 text-4xl font-bold text-navy-700 dark:text-white">Sign In</h4>
        <div className="text-red-600">{apiResError}</div>
        <InputField
          control={control}
          name={LoginFormFieldsName.EMAIL}
          variant="auth"
          extra="mb-3"
          label="Email*"
          placeholder="mail@mail.com"
          id="email"
          type="text"
        />
        <InputField
          control={control}
          name={LoginFormFieldsName.PASSWORD}
          variant="auth"
          extra="mb-3"
          label="Password*"
          placeholder="Min. 8 characters"
          id="password"
          type="password"
        />
        <div className="mb-4 flex items-center justify-end px-2">
          <Link
            className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
            to="/admin/auth/forgot-password"
          >
            Forgot Password?
          </Link>
        </div>
        <button
          type="submit"
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          disabled={!formState.isValid || loading} // Disable button while loading
        >
          Sign In
        </button>
        <div className="mt-4">
          <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
            Not registered yet?
          </span>
          <Link
            to="/admin/auth/sign-up"
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Create an account
          </Link>
        </div>

        <style jsx>{`
          .loader-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 9999; /* Ensure it's on top of other content */
          }

          .loader {
            border: 8px solid rgba(0, 0, 0, 0.1); /* Light grey background */
            border-radius: 50%;
            border-top: 8px solid white; /* Blue color for the spinner */
            width: 50px;
            height: 50px;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </form>
    </div>
  );
}
